import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { WebSocketService } from './core/services/websocket.service';
import { NetworkStatusService } from './core/services/network-status.service';
import { Subscription } from 'rxjs';
import { WorkBoxService } from './core/services/workbox.service';
import { env } from '../environments/env';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  loading = true;
  private subscription = new Subscription();
  private readonly swStartupTimeoutMs = 5000;

  constructor(
    private webSocketService: WebSocketService,
    private networkStatusService: NetworkStatusService,
    private workBoxService: WorkBoxService,
  ) {}

  async ngOnInit(): Promise<void> {
    void this.initializeServiceWorker();

    //await this.userService.load();

    this.subscription.add(
      this.networkStatusService.isOnline().subscribe((online) => {
        online ? this.goOnline() : this.goOffline();
      }),
    );

    this.loading = false;
    this.preventOutsidePinchZoom();
  }

  private async initializeServiceWorker(): Promise<void> {
    if ('serviceWorker' in navigator && env.get().serviceWorker) {
      try {
        await this.withTimeout(this.workBoxService.register(), this.swStartupTimeoutMs, 'register');
        await this.withTimeout(navigator.serviceWorker.ready, this.swStartupTimeoutMs, 'ready');
        await this.withTimeout(this.workBoxService.getServiceWorkerVersion(), this.swStartupTimeoutMs, 'version');
      } catch (error) {
        console.warn('[SW] Service worker startup failed. App will continue without SW features.', error);
      }
    }
  }

  private async withTimeout<T>(promise: Promise<T>, timeoutMs: number, step: string): Promise<T> {
    return await new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error(`[SW] Startup step timed out: ${step}`)), timeoutMs);

      promise
        .then((value) => {
          clearTimeout(timer);
          resolve(value);
        })
        .catch((error) => {
          clearTimeout(timer);
          reject(error);
        });
    });
  }

  private preventOutsidePinchZoom(): void {
    const insideViewer = (t: EventTarget | null): boolean =>
      !!(t as HTMLElement)?.closest?.('.pdf-viewer-wrapper, ngx-extended-pdf-viewer');

    ['gesturestart', 'gesturechange', 'gestureend'].forEach((evt) =>
      document.addEventListener(
        evt,
        (e: Event) => {
          if (!insideViewer(e.target)) {
            e.preventDefault();
          }
        },
        { passive: false },
      ),
    );

    document.addEventListener(
      'touchmove',
      (e: TouchEvent) => {
        if (e.touches.length > 1 && !insideViewer(e.target)) {
          e.preventDefault();
        }
      },
      { passive: false },
    );
  }

  ngOnDestroy() {
    this.webSocketService.disconnect();
    this.subscription.unsubscribe();
  }

  goOnline() {
    this.webSocketService.connect();
  }

  goOffline() {
    this.webSocketService.disconnect();
  }
}
